import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type RefObject,
} from 'react';
import { Label } from '../Label/Label';
import { ScrollBar } from '../ScrollBar/ScrollBar';
import { mergeRefs } from '../../utils/mergeRefs';
import type { InputTextAreaFill, InputTextAreaProps } from './InputTextArea.types';
import './InputTextArea.css';

export const InputTextArea = forwardRef<HTMLTextAreaElement, InputTextAreaProps>(function InputTextArea({
  label = 'Label',
  placeholder = 'Input',
  helperText = 'Helper text',
  value,
  defaultValue,
  size = 'medium',
  fill,
  showLabel = true,
  showAsterisk = false,
  showHelpIcon = false,
  showHelperText = false,
  showCharCount = false,
  charCount,
  maxChars = 40,
  disabled = false,
  readOnly = false,
  danger = false,
  stretch = false,
  visualState,
  showFocus = false,
  endIcon,
  attachments,
  className = '',
  onChange,
  style,
  ...textareaProps
}, ref) {
  const fieldId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [internalValue, setInternalValue] = useState(value ?? defaultValue ?? '');
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const resolvedFill: InputTextAreaFill = fill ?? (currentValue.length > 0 ? 'filled' : 'empty');
  const resolvedCharCount = charCount ?? currentValue.length;
  const resolvedState = disabled ? 'disabled' : visualState ?? 'default';
  const isFocused = showFocus || resolvedState === 'pressed';
  const showCountBar = showCharCount;
  const hasAttachments = Boolean(attachments);

  // The ScrollBar overlay is absolutely positioned over the whole bordered box;
  // when an attachments row sits under the textarea, measure it so the track
  // stops above the row instead of running over the thumbnails.
  const attachmentsRef = useRef<HTMLDivElement>(null);
  const [attachmentsHeight, setAttachmentsHeight] = useState(0);
  useLayoutEffect(() => {
    const el = attachmentsRef.current;
    if (!el) {
      setAttachmentsHeight(0);
      return undefined;
    }
    const update = () => setAttachmentsHeight(el.offsetHeight);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAttachments]);

  const rootClasses = [
    'spark-input-textarea',
    `spark-input-textarea-${size}`,
    `spark-input-textarea-fill-${resolvedFill}`,
    `spark-input-textarea-state-${resolvedState}`,
    disabled ? 'spark-input-textarea-disabled' : '',
    readOnly ? 'spark-input-textarea-readonly' : '',
    danger ? 'spark-input-textarea-danger' : '',
    stretch ? 'spark-input-textarea-stretch' : '',
    isFocused ? 'spark-input-textarea-show-focus' : '',
    endIcon ? 'spark-input-textarea-has-end-icon' : '',
    hasAttachments ? 'spark-input-textarea-has-attachments' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const boxClasses = [
    'spark-input-textarea-box',
    showCountBar ? 'spark-input-textarea-has-count' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const boxStyle = hasAttachments
    ? ({ '--spark-input-textarea-attachments-height': `${attachmentsHeight}px` } as CSSProperties)
    : undefined;

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }
    onChange?.(event);
  };

  return (
    <div className={rootClasses} style={style}>
      {showLabel ? (
        <Label
          label={label}
          htmlFor={fieldId}
          showAsterisk={showAsterisk}
          showIcon={showHelpIcon}
        />
      ) : null}
      <div className="spark-input-textarea-wrap">
        <div className={boxClasses} style={boxStyle}>
          <textarea
            ref={mergeRefs(ref, fieldRef)}
            id={fieldId}
            className="spark-input-textarea-field"
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            value={currentValue}
            onChange={handleChange}
            {...textareaProps}
          />
          {showCountBar ? (
            <div className="spark-input-textarea-show-count">
              <span className="spark-input-textarea-count" aria-live="polite">
                <span>{resolvedCharCount}</span>
                <span>/</span>
                <span>{maxChars}</span>
              </span>
            </div>
          ) : null}
          <ScrollBar
            containerRef={fieldRef as RefObject<HTMLElement | null>}
            className="spark-input-textarea-scrollbar"
          />
          {endIcon ? (
            <span className="spark-input-textarea-end-icon">{endIcon}</span>
          ) : null}
          {hasAttachments ? (
            <div ref={attachmentsRef} className="spark-input-textarea-attachments">
              {attachments}
            </div>
          ) : null}
        </div>
        <span className="spark-input-textarea-focus" aria-hidden="true" />
      </div>
      {showHelperText ? <span className="spark-input-textarea-helper">{helperText}</span> : null}
    </div>
  );
});

InputTextArea.displayName = 'InputTextArea';
